import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './WelcomeHeader.module.css';
import LanguageSwitcher from '../common/LanguageSwitcher';

function WelcomeHeader({ loggedIn = false, username = '' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState('dark');

  useEffect(() => {
    // Load theme from localStorage
    const savedTheme = localStorage.getItem('theme') || 'dark';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleGoToApp = () => {
    navigate('/mein-regal');
  };

  const handleFeaturesClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      // Hash nach Navigation setzen, Welcome-Seite liest ihn aus
      setTimeout(() => {
        document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    }
  };

  // Ersten Buchstaben für Avatar
  const getInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : '?';
  };

  return (
    <header className={styles.welcomeHeader}>
      <div className={styles.headerContent}>
        {/* Logo + Name */}
        <Link to={loggedIn ? "/mein-regal" : "/"} className={styles.logoSection}>
          <div className={styles.logo}>
            <img src={require('../../assets/logo_1.png')} alt="MangaShelf" style={{width:40,height:40,borderRadius:9,objectFit:'contain'}} />
          </div>
          <span className={styles.siteName}>Manga<span className={styles.siteNameAccent}>Shelf</span></span>
        </Link>

        {/* Navigation rechts */}
        <nav className={styles.navSection}>
          <Link to="/about" className={styles.navLink}>{t('nav.about')}</Link>
          <a href="/#features" onClick={handleFeaturesClick} className={styles.navLink}>{t('nav.features')}</a>
          <Link to="/contact" className={styles.navLink}>{t('nav.support')}</Link>

          <LanguageSwitcher className={styles.langSwitcher} />

          <a 
            href="https://github.com/KilianJATO/MangaOrganisation" 
            target="_blank" 
            rel="noopener noreferrer"
            className={styles.githubLink}
            title="View on GitHub"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M10 0C4.477 0 0 4.477 0 10c0 4.42 2.865 8.17 6.839 9.49.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0110 4.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C17.137 18.167 20 14.418 20 10c0-5.523-4.477-10-10-10z"/>
            </svg>
          </a>
          
          <button 
            className={styles.themeToggle} 
            onClick={toggleTheme}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="4" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10 2V4M10 16V18M18 10H16M4 10H2M15.5 4.5L14 6M6 14L4.5 15.5M15.5 15.5L14 14M6 6L4.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"/>
              </svg>
            )}
          </button>
          
          {/* Zeige User-Info wenn eingeloggt, sonst Login-Button */}
          {loggedIn ? (
            <button className={styles.userButton} onClick={handleGoToApp} title={`Eingeloggt als ${username}`}>
              <div className={styles.userAvatar}>
                {getInitial(username)}
              </div>
              <span className={styles.userName}>{username}</span>
            </button>
          ) : (
            <button className={styles.loginButton} onClick={handleLogin}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="9" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M3 16C3 12.686 5.686 10 9 10C12.314 10 15 12.686 15 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              {t('nav.login')}
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}

export default WelcomeHeader;
